import React, { useMemo, useState } from 'react';
import '../pages/ManageSMRTCOP.css';

interface SubscriptionData {
  orgID: string;
  companyName: string;
  displayName: string;
  planName: string;
  planFeatures: Record<string, { enabled: boolean }>;
  overrideFeatures: Record<string, { enabled: boolean }>;
  lastUpdated: string;
  createdAt?: string;
  defaultModule?: string;
  billingAdmin?: string;
  ownerName?: string;
  ownerEmail?: string;
  lastAccessedAt?: string;
  lastAccessedBy?: string;
  lastAccessedByEmail?: string;
}

interface OrganizationsTableProps {
  subscriptions: SubscriptionData[];
  onView: (orgID: string) => void;
  onEdit: (orgID: string) => void;
  onDelete: (orgID: string, companyName: string) => void;
}

type SortKey = 'orgName' | 'legalName' | 'ownerName' | 'ownerEmail' | 'createdAt' | 'lastAccessed';
type SortDir = 'asc' | 'desc';

function formatDate(value?: string): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

function formatDateTime(value?: string): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

function getSortValue(
  sub: SubscriptionData,
  key: SortKey,
): string | number {
  switch (key) {
    case 'orgName':
      return (sub.displayName || sub.companyName || '').toLowerCase();
    case 'legalName':
      return (sub.companyName || '').toLowerCase();
    case 'ownerName':
      return (sub.ownerName || '').toLowerCase();
    case 'ownerEmail':
      return (sub.ownerEmail || sub.billingAdmin || '').toLowerCase();
    case 'createdAt':
      return sub.createdAt ? new Date(sub.createdAt).getTime() : 0;
    case 'lastAccessed':
      return sub.lastAccessedAt ? new Date(sub.lastAccessedAt).getTime() : 0;
  }
}

function SortArrow({ active, dir }: { active: boolean; dir: SortDir }) {
  if (!active) {
    return <span style={{ marginLeft: 4, color: '#555', fontSize: '0.7rem' }}>↕</span>;
  }
  return (
    <span style={{ marginLeft: 4, color: '#fff', fontSize: '0.7rem' }}>
      {dir === 'asc' ? '▲' : '▼'}
    </span>
  );
}

export default function OrganizationsTable({
  subscriptions,
  onView,
  onEdit,
  onDelete,
}: OrganizationsTableProps) {
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const handleSort = (key: SortKey) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir('asc');
      return;
    }
    if (sortDir === 'asc') {
      setSortDir('desc');
      return;
    }
    setSortKey(null);
    setSortDir('asc');
  };

  const sortedSubs = useMemo(() => {
    if (!sortKey) return subscriptions;
    const copy = [...subscriptions];
    copy.sort((a, b) => {
      const av = getSortValue(a, sortKey);
      const bv = getSortValue(b, sortKey);
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [subscriptions, sortKey, sortDir]);

  const headerCellStyle: React.CSSProperties = {
    cursor: 'pointer',
    userSelect: 'none',
    display: 'flex',
    alignItems: 'center',
  };

  return (
    <div>
      <div style={{ background: '#1e1e1e', borderRadius: 8, padding: 12 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.2fr 1.2fr 1fr 1.3fr 0.9fr 1.3fr 130px',
            gap: 12,
            padding: '8px 12px',
            background: '#1a1a1a',
            borderRadius: 6,
            fontSize: '0.8rem',
            fontWeight: 600,
            color: '#9aa0a6',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            marginBottom: 4,
            position: 'sticky',
            top: 0,
            zIndex: 5,
            boxShadow: '0 2px 6px rgba(0,0,0,0.5)',
          }}
        >
          <span style={headerCellStyle} onClick={() => handleSort('orgName')}>
            Organization Name <SortArrow active={sortKey === 'orgName'} dir={sortDir} />
          </span>
          <span style={headerCellStyle} onClick={() => handleSort('legalName')}>
            Legal Entity Name <SortArrow active={sortKey === 'legalName'} dir={sortDir} />
          </span>
          <span style={headerCellStyle} onClick={() => handleSort('ownerName')}>
            Owner Name <SortArrow active={sortKey === 'ownerName'} dir={sortDir} />
          </span>
          <span style={headerCellStyle} onClick={() => handleSort('ownerEmail')}>
            Owner Email <SortArrow active={sortKey === 'ownerEmail'} dir={sortDir} />
          </span>
          <span style={headerCellStyle} onClick={() => handleSort('createdAt')}>
            Created Date <SortArrow active={sortKey === 'createdAt'} dir={sortDir} />
          </span>
          <span style={headerCellStyle} onClick={() => handleSort('lastAccessed')}>
            Last Accessed <SortArrow active={sortKey === 'lastAccessed'} dir={sortDir} />
          </span>
          <span style={{ textAlign: 'center' }}>Actions</span>
        </div>

        {sortedSubs.length === 0 ? (
          <div style={{ padding: '20px 12px', color: '#9aa0a6', fontSize: '0.9rem', textAlign: 'center' }}>
            No organizations found.
          </div>
        ) : (
          sortedSubs.map((sub) => (
            <div
              key={sub.orgID}
              className="smrtcop-overview-row"
              style={{
                gridTemplateColumns: '1.2fr 1.2fr 1fr 1.3fr 0.9fr 1.3fr 130px',
                fontSize: '0.9rem',
              }}
            >
              <span style={{ fontWeight: 600 }}>{sub.displayName || sub.companyName}</span>
              <span style={{ color: '#cbd5e1' }}>{sub.companyName || '—'}</span>
              <span style={{ color: '#cbd5e1' }}>{sub.ownerName || '—'}</span>
              <span style={{ color: '#cbd5e1' }}>{sub.ownerEmail || sub.billingAdmin || '—'}</span>
              <span style={{ color: '#cbd5e1' }}>{formatDate(sub.createdAt)}</span>
              <span style={{ color: '#cbd5e1' }} title={sub.lastAccessedByEmail || sub.lastAccessedBy || ''}>
                {formatDateTime(sub.lastAccessedAt)}
                {sub.lastAccessedByEmail && (
                  <div style={{ fontSize: '0.75rem', color: '#9aa0a6', marginTop: 2 }}>
                    {sub.lastAccessedByEmail}
                  </div>
                )}
              </span>
              <div className="smrtcop-actions">
                <button
                  className="smrtcop-btn-icon smrtcop-btn-view"
                  onClick={() => onView(sub.orgID)}
                  title="View organization"
                >
                  👁
                </button>
                <button
                  className="smrtcop-btn-icon smrtcop-btn-edit"
                  onClick={() => onEdit(sub.orgID)}
                  title="Edit organization"
                >
                  ✎
                </button>
                <button
                  className="smrtcop-btn-icon smrtcop-btn-delete"
                  onClick={() => onDelete(sub.orgID, sub.companyName)}
                  title="Delete organization"
                >
                  🗑
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
